/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useTranslation } from "react-i18next";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { cn } from "~/lib/utils";
import ClientOnly from "../shared-components/ClentOnly";
import { FALLBACK_IMAGE_ALT_TEXT } from "~/utils/constant";

// Skeleton component for server-side rendering
function OurAppsSkeleton({
  className,
  width = 192,
  height = 192,
  backGroundedColor,
}: {
  className?: string;
  width?: number | string;
  height?: number | string;
  backGroundedColor?: boolean;
}) {
  const skeletonItems = [1, 2, 3, 4]; // 3 skeleton cards

  return (
    <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 justify-center items-center", className)}>
      {skeletonItems.map((item) => (
        <div
          key={item}
          className={cn("w-auto text-center rounded-xl p-4 flex flex-col justify-center items-center", backGroundedColor && "bg-darkBackground")}
        >
          <div className="rounded-lg overflow-hidden flex items-center justify-center mb-2 bg-gray-300 animate-pulse" style={{ width, height }} />
          <div className="w-full space-y-2">
            <div className="h-4 bg-gray-300 rounded animate-pulse w-3/4 mx-auto" />
            <div className="h-4 bg-gray-300 rounded animate-pulse w-2/3 mx-auto" />
          </div>
        </div>
      ))}
    </div>
  );
}

function OurAppsContent({
  className,
  width = 192,
  height = 192,
  textSize = 16,
  textColor = "textColor",
  backGroundedColor,
}: {
  className?: string;
  width?: number | string;
  height?: number | string;
  textSize?: number | string;
  textColor?: string;
  backGroundedColor?: boolean;
}) {
  const { LocalizedLink } = useLocale();
  const { normalInter } = useFontStyle();
  const { t } = useTranslation();
  const apps = [
    {
      line1: t("footer.swaminarayan", { defaultValue: "Swaminarayan" }),
      line2: t("footer.satsang_app", { defaultValue: "Satsang App" }),
      image: "https://play-lh.googleusercontent.com/dx_fRnUTWXiwA-CghUaGImJBaaZNw4pVOcoXKrAE3lylhW8CCbCu9ckM289FAIO9Jg=w240-h480-rw",
      info: "swaminarayan-satsang-app",
      // navigate: () => navigate(`/satsang-app`),
      navigate: `/satsang-app`,
    },
    {
      line1: t("footer.swaminarayan", { defaultValue: "Swaminarayan" }),
      line2: t("footer.hari_app", { defaultValue: "Hari App" }),
      image: "https://play-lh.googleusercontent.com/8uENrxAoXLPNmcOyB76lZNmfujlxy_5G5Mh3E_uNHla8ECNDqG5WE3qjDCPGTr6bTA=w240-h480-rw",
      info: "hari-swaminarayan-game",
      navigate: `/hari-game`,
    },
    {
      line1: t("footer.adhyatmik", { defaultValue: "Adhyatmik" }),
      line2: t("footer.hisab_app", { defaultValue: "Hisab App" }),
      image: "https://play-lh.googleusercontent.com/u6qDQXSWmoPAtnbzX163sdDqFbgLPZc9XxbBcxnpuNVMlidY_SqXSBdHQPLgVxBdWws=w240-h480-rw",
      info: "adhyatmik-hisab",
      navigate: `/adhyatmik-hisab`,
    },
    {
      line1: t("footer.saang", { defaultValue: "Saang" }),
      line2: t("footer.dhyan", { defaultValue: "Dhyan" }),
      image: "https://play-lh.googleusercontent.com/YDJ4zm90H3YW-b2tKj8Cblz08dmpBT0oNOWPoJy3OU3GxZc-WLHoghxShUacw_9nEEtI0_B7ataUxrKx7VkuIQ=s64-rw",
      info: "saang-dhyan",
      navigate: `/saang-dhyan`,
    },
  ];

  return (
    <div className={cn("grid grid-cols-1 sm:grid-cols-2 justify-center items-center md:gap-x-5", className)}>
      {apps.map((app) => (
        <LocalizedLink
          key={app.info}
          className={cn(
            "w-auto text-center rounded-xl p-2 2xl:p-4 cursor-pointer transition-transform flex flex-col justify-center items-center",
            backGroundedColor && "bg-darkBackground",
          )}
          to={app.navigate}
        >
          <div
            className="rounded-lg overflow-hidden flex items-center justify-center mb-2"
            style={{ width, height, backgroundColor: "gray" }} // fallback bg-red-400
          >
            <img
              src={app.image}
              title={app.info || FALLBACK_IMAGE_ALT_TEXT}
              alt={app.info || FALLBACK_IMAGE_ALT_TEXT}
              className="w-full h-full object-cover select-none"
            />
          </div>
          <p
            className={cn(
              "text-textColor font-normal line-clamp-2 ",
              `text-${textColor}`,
              normalInter,
              backGroundedColor ? "body_text_l" : "body_text_xs",
            )}
          >
            {app.line1}
            <br />
            {app.line2}
          </p>
        </LocalizedLink>
      ))}
    </div>
  );
}

// Main component that wraps content with ClientOnly
function OurApps({
  className,
  width = 192,
  height = 192,
  textSize = 16,
  textColor = "textColor",
  backGroundedColor,
}: {
  className?: string;
  width?: number | string;
  height?: number | string;
  textSize?: number | string;
  textColor?: string;
  backGroundedColor?: boolean;
}) {
  return (
    <ClientOnly
      fallback={
        <OurAppsContent
          className={className}
          width={width}
          height={height}
          textSize={textSize}
          textColor={textColor}
          backGroundedColor={backGroundedColor}
        />
      }
    >
      <OurAppsContent
        className={className}
        width={width}
        height={height}
        textSize={textSize}
        textColor={textColor}
        backGroundedColor={backGroundedColor}
      />
    </ClientOnly>
  );
}

export default OurApps;
